import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  BillingPackage,
  InvoiceDoc,
  PaginatedInvoices,
} from '../../api/backofficeBilling';

export interface BillingState {
  loading: boolean;
  changingPlan: boolean;
  error?: string | null;
  packages: BillingPackage[];
  selectedPlanId?: string | null;
  billingInterval: 'monthly' | 'yearly';
  invoices: PaginatedInvoices;
  currentPlanId?: string | null;
  currentPlanMonthlyCost?: number | null;
}

const initialInvoices: PaginatedInvoices = {
  docs: [],
  totalDocs: 0,
  totalPages: 0,
  page: 1,
  perPage: 100,
};

const initialState: BillingState = {
  loading: false,
  changingPlan: false,
  error: null,
  packages: [],
  selectedPlanId: null,
  billingInterval: 'monthly',
  invoices: initialInvoices,
  currentPlanId: null,
  currentPlanMonthlyCost: null,
};

const billingSlice = createSlice({
  name: 'backofficeBilling',
  initialState,
  reducers: {
    setBillingLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setBillingError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setChangingPlan: (state, action: PayloadAction<boolean>) => {
      state.changingPlan = action.payload;
    },
    setPackages: (state, action: PayloadAction<BillingPackage[]>) => {
      state.packages = Array.isArray(action.payload) ? action.payload : [];
    },
    setSelectedPlanId: (
      state,
      action: PayloadAction<string | null | undefined>,
    ) => {
      state.selectedPlanId = action.payload ?? null;
    },
    setBillingInterval: (
      state,
      action: PayloadAction<'monthly' | 'yearly'>,
    ) => {
      state.billingInterval = action.payload;
    },
    setInvoices: (state, action: PayloadAction<PaginatedInvoices>) => {
      const payload = action.payload || initialInvoices;
      state.invoices = {
        ...state.invoices,
        ...payload,
        docs: Array.isArray(payload.docs) ? payload.docs : [],
        perPage:
          typeof payload.perPage === 'number'
            ? payload.perPage
            : ((payload as any)?.limit ?? state.invoices.perPage),
      } as PaginatedInvoices;
    },
    setCurrentPlan: (
      state,
      action: PayloadAction<{ id: string | null; monthlyCost: number | null }>,
    ) => {
      state.currentPlanId = action.payload.id ?? null;
      state.currentPlanMonthlyCost = action.payload.monthlyCost ?? null;
    },
    clearInvoices: state => {
      state.invoices = initialInvoices;
    },
  },
});

export const {
  setBillingLoading,
  setBillingError,
  setChangingPlan,
  setPackages,
  setSelectedPlanId,
  setBillingInterval,
  setInvoices,
  setCurrentPlan,
  clearInvoices,
} = billingSlice.actions;

export default billingSlice.reducer;
